import { useEffect } from 'react';
import { fullstoryEvent } from '@/fullstory/utils/fullstory-event';
import { validateEmail } from '@/form/utils/validate-email';
import { validatePhone } from '@/common/utils/validate-phone';

interface DigiohMailboxCallbacks {
  pageSource: string;
  ready: boolean;
}

/** This hook provides callbacks to Digioh which can be used to track when a Digioh email box is opened or closed.
 *  It sends an event to Fullstory with the box_id and whether an email or phone was acquired.
 *
 *  In further iterations, we will also store the email and phone to our BE along with ad tracking.
 *  Because this method is accessible globally, I'm adding some stricter regex validation than a usual input.
 *
 * @param {string} DigiohMailboxCallbacks.pageSource - The human-readable name of the page.
 * @param {boolean} DigiohMailboxCallbacks.ready - Whether the Digioh API is ready from useDigiohAPIReady.
 */
export const useDigiohMailboxCallbacks = ({
  pageSource,
  ready,
}: DigiohMailboxCallbacks) => {
  useEffect(() => {
    if (ready && window.DIGIOH_API) {
      window.DIGIOH_API.openDigiohEmailCallback = (boxId: string | number) => {
        const boxIdValid = typeof boxId === 'number';

        fullstoryEvent('digioh_email_box_opened', {
          box_id: boxIdValid ? boxId : 0,
          page_source: pageSource,
        });
      };

      window.DIGIOH_API.closeDigiohEmailCallback = (
        email: string,
        phone: string | number,
        boxId: string | number
      ) => {
        const { valid: emailValid } = validateEmail(String(email));
        const { valid: phoneValid } = validatePhone(String(phone));
        const boxIdValid = typeof boxId === 'number';

        fullstoryEvent('digioh_email_box_closed', {
          acquired_email: emailValid,
          acquired_phone: phoneValid,
          box_id: boxIdValid ? boxId : 0,
          page_source: pageSource,
        });
      };
    }
  }, [ready, pageSource]);
};
